// CityPage.jsx - Dynamic City/Location Landing Pages for Local SEO
"use client";
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Star, Check, Calendar, ArrowRight } from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import { generateCityPageMeta, generateClinicSchema, generateBreadcrumbSchema } from "../utils/seo";
import { Button } from "./ui/button";

export default function CityPage() {
  const { city } = useParams();
  const navigate = useNavigate();

  // Parse city name from URL slug
  const cityName = city ? city.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Toronto';
  const province = 'ON'; // Could be made dynamic

  // SEO Meta Tags
  const seoMeta = generateCityPageMeta(cityName, province);

  // Mock clinic data - in production, this would come from API
  const clinics = [
    {
      id: 1,
      name: `${cityName} Wellness Centre`,
      slug: `${city}-wellness-centre`,
      address: `123 Main Street, ${cityName}, ${province}`,
      phone: '+1 (416) 555-0100',
      rating: { average: 4.8, count: 156 },
      image: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=600&auto=format&fit=crop&q=80',
      services: ['Deep Tissue', 'Swedish', 'Sports Massage', 'Hot Stone'],
      description: `Premium massage therapy services in the heart of ${cityName}`,
      coordinates: { lat: 43.6532, lng: -79.3832 }
    },
    {
      id: 2,
      name: `${cityName} Massage Therapy`,
      slug: `${city}-massage-therapy`,
      address: `456 Oak Avenue, ${cityName}, ${province}`,
      phone: '+1 (416) 555-0200',
      rating: { average: 4.9, count: 203 },
      image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&auto=format&fit=crop&q=80',
      services: ['Prenatal', 'Aromatherapy', 'Reflexology', 'Thai Massage'],
      description: `Certified RMTs providing personalized care in ${cityName}`,
      coordinates: { lat: 43.6552, lng: -79.3850 }
    },
    {
      id: 3,
      name: `${cityName} Sports Therapy Clinic`,
      slug: `${city}-sports-therapy`,
      address: `789 Elm Street, ${cityName}, ${province}`,
      phone: '+1 (416) 555-0300',
      rating: { average: 4.7, count: 98 },
      image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=600&auto=format&fit=crop&q=80',
      services: ['Sports Massage', 'Deep Tissue', 'Injury Recovery'],
      description: `Specialized sports massage therapy for athletes in ${cityName}`,
      coordinates: { lat: 43.6500, lng: -79.3900 }
    }
  ];

  // Breadcrumb schema
  const breadcrumbs = [
    { name: "Home", url: "https://bookatherapy.com" },
    { name: "Locations", url: "https://bookatherapy.com/locations" },
    { name: cityName, url: `https://bookatherapy.com/locations/${city}` }
  ];

  // Combine schemas
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      generateBreadcrumbSchema(breadcrumbs),
      ...clinics.map(clinic => generateClinicSchema({
        ...clinic,
        city: cityName,
        province,
        postal_code: 'M5V 3A8' // Mock postal code
      }))
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={seoMeta.title}
        description={seoMeta.description}
        keywords={seoMeta.keywords}
        canonical={seoMeta.canonical}
        ogTitle={seoMeta.ogTitle}
        ogDescription={seoMeta.ogDescription}
        schema={combinedSchema}
      />

      <NavbarDemo />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 text-[#98C454] mb-4">
              <MapPin className="h-6 w-6" />
              <span className="text-lg">{cityName}, {province}</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Professional Massage Therapy in <span className="text-[#98C454]">{cityName}</span>
            </h1>

            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Book certified RMTs for home, mobile, or clinic massage services in {cityName}.
              Transparent pricing, same-day availability, personalized matching.
            </p>

            <Button
              size="lg"
              onClick={() => navigate("/book")}
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-10 py-6 text-lg rounded-xl font-semibold"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book in {cityName}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us in {cityName} */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#252F4F] text-center mb-12">
            Why Book Massage Therapy in {cityName}?
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: `${clinics.length}+ Partner Clinics`,
                description: `Access to the best massage therapy clinics across ${cityName}`,
                icon: <MapPin className="h-8 w-8" />
              },
              {
                title: "Certified RMTs",
                description: "All therapists are registered, licensed, and insured",
                icon: <Check className="h-8 w-8" />
              },
              {
                title: "Same-Day Booking",
                description: `Book your massage appointment in ${cityName} within minutes`,
                icon: <Clock className="h-8 w-8" />
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#98C454] hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#252F4F] rounded-xl flex items-center justify-center text-[#98C454] mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#252F4F] mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinics Listings */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#252F4F] mb-12">
            Top Massage Therapy Clinics in {cityName}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {clinics.map((clinic, index) => (
              <motion.div
                key={clinic.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                onClick={() => navigate(`/clinics/${clinic.slug}`)}
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#98C454]"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={clinic.image}
                    alt={clinic.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span className="font-bold text-sm">{clinic.rating.average}</span>
                    <span className="text-xs text-gray-500">({clinic.rating.count})</span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#252F4F] mb-2 group-hover:text-[#98C454] transition-colors">
                    {clinic.name}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4">{clinic.description}</p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#98C454]" />
                      <span>{clinic.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4 text-[#98C454]" />
                      <span>{clinic.phone}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {clinic.services.slice(0, 3).map((service, idx) => (
                      <span key={idx} className="bg-[#252F4F] text-[#98C454] text-xs px-3 py-1 rounded-full">
                        {service}
                      </span>
                    ))}
                  </div>

                  <Button
                    className="w-full bg-[#252F4F] hover:bg-[#98C454] text-white hover:text-[#252F4F] transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/book");
                    }}
                  >
                    Book Now
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services in {cityName} */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-[#252F4F] mb-8">
            Massage Services Available in {cityName}
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Deep Tissue Massage',
              'Swedish Massage',
              'Sports Massage',
              'Prenatal Massage',
              'Hot Stone Therapy',
              'Aromatherapy',
              'Reflexology',
              'Thai Massage'
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-4 hover:border-[#98C454] hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate("/book")}
              >
                <div className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-[#98C454]" />
                  <span className="font-medium text-[#252F4F]">{service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local SEO Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-[#252F4F] mb-6">
              About Massage Therapy in {cityName}, {province}
            </h2>

            <p className="text-gray-700 mb-4">
              {cityName} offers some of the best massage therapy services in {province}. Whether you're looking for
              therapeutic deep tissue massage, relaxing Swedish massage, or specialized sports therapy, {cityName} has
              certified Registered Massage Therapists (RMTs) ready to help you achieve your wellness goals.
            </p>

            <p className="text-gray-700 mb-4">
              BookaTherapy makes it easy to find and book massage appointments in {cityName}. Our platform connects
              you with verified, licensed RMTs who offer flexible booking options including home studio visits, mobile
              therapy (therapists come to you), and traditional clinic appointments.
            </p>

            <h3 className="text-2xl font-bold text-[#252F4F] mt-8 mb-4">
              Finding the Right Massage Therapist in {cityName}
            </h3>

            <p className="text-gray-700 mb-4">
              Our personalized matching system helps you find the perfect massage therapist in {cityName} based on
              your specific needs, preferences, and location. All therapists on our platform are:
            </p>

            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-[#98C454] mt-1 flex-shrink-0" />
                <span className="text-gray-700">Certified and licensed Registered Massage Therapists (RMT)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-[#98C454] mt-1 flex-shrink-0" />
                <span className="text-gray-700">Fully insured and background-checked</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-[#98C454] mt-1 flex-shrink-0" />
                <span className="text-gray-700">Experienced in various massage modalities</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="h-5 w-5 text-[#98C454] mt-1 flex-shrink-0" />
                <span className="text-gray-700">Highly rated by {cityName} residents</span>
              </li>
            </ul>

            <h3 className="text-2xl font-bold text-[#252F4F] mt-8 mb-4">
              Massage Therapy Pricing in {cityName}
            </h3>

            <p className="text-gray-700 mb-4">
              We believe in transparent pricing with no hidden fees. Standard massage therapy sessions in {cityName}
              are priced at $100 for 60 minutes (plus GST) for home studio and clinic appointments, and $120 for
              mobile therapy where the therapist comes to your location.
            </p>
          </article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#252F4F] to-[#1e2640]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Book Your Massage in {cityName}?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Find the perfect massage therapist and book your appointment today
            </p>
            <Button
              size="lg"
              onClick={() => navigate("/book")}
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-10 py-6 text-lg rounded-xl font-semibold"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Book in {cityName} Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
